import { isCanonicalRole } from "@/data/canonical";
import type { RoleRegistry } from "@/data/roleRegistry";
import { activeModifiers, gateEvaluation, type HookScope, type ModifierDefinition, type ModifierGate } from "@/abilities/modifiers";
import { resolveAbilitySemantics, type AbilitySemanticsRegistry } from "@/abilities/semantics";
import { currentLiveMoment, lifeEventsAt, type LifeEventQueryResult } from "./lifeEvents";
import { gameRuleFactActive, gameRuleFactDefinition } from "./gameRuleFacts";
import type { ParticipantBinding } from "./abilityResolution";
import type { Alignment, EffectRecord, LifeEvent, LiveGameMoment, RoleDef, RoleId, STPlayerRecord, Script, StorytellerLobbyRecord } from "./types";

/**
 * Phase 10F: the Rules Query layer (PHASE10F Section 3.2) -- pure, DERIVED
 * answers ability evaluators need. Nothing computed here is ever persisted:
 * Effect `state` stays lifecycle state, never cached applicability
 * (10F-AC-13). Every answer is either KNOWN (one mechanically correct answer
 * from authoritative Current State) or UNKNOWN with a reason -- an unknown,
 * custom, cyclic or unmodeled interaction is never resolved by invention; the
 * evaluator turns it into an explicit Storyteller judgment.
 *
 * Reminders are never read (architecture-guarded): they are notation, not
 * truth. History is never read: Current State and the Life Event Window are
 * the only mechanical sources. Phase 10G: game-scoped Rule Facts are read from
 * Current State (`gameRuleFacts`) the same way -- never from History, Reminders
 * or the Activity presentation.
 */

export type QueryAnswer<T> =
  | { known: true; value: T }
  | { known: false; reason: string };

const known = <T,>(value: T): QueryAnswer<T> => ({ known: true, value });
const unknown = <T,>(reason: string): QueryAnswer<T> => ({ known: false, reason });

/**
 * The APPROVED semantic interpretation of known Effect types -- the one place
 * a stored Effect type acquires mechanical meaning. Kept apart from the
 * presentation registry (which never decides mechanics). An unlisted / custom
 * type acquires NO rule by name: it is always "unknown".
 *
 *  - impairment: the bearer has no functioning ability (Drunk/Poisoned:
 *    "has no ability but believes they do").
 *  - noAbility: the bearer's ability is lost.
 *  - demonProtection / deathImmunity: distinct protection semantics (Safe
 *    from the Demon vs Cannot die) -- never conflated (10F-AC-14).
 *  - genericProtection: the generic manual "Protected" marker. It NEVER means
 *    "cannot die" automatically; any death question needs a Storyteller
 *    decision.
 *  - judgment: an interaction that always needs a Storyteller decision.
 *  - storytellerFact: Phase 10F Slice 7 -- Storyteller-private authoritative
 *    state recorded as an Effect (e.g. the Fortune Teller's Red Herring,
 *    matrix Section 7). It applies exactly while active, with NO dependence on
 *    any source functioning; only the evaluator that owns the fact decides
 *    when it matters. It is never a Reminder.
 */
export type EffectSemantics = "impairment" | "noAbility" | "demonProtection" | "deathImmunity" | "genericProtection" | "judgment" | "storytellerFact";
export const APPROVED_EFFECT_SEMANTICS: Readonly<Record<string, EffectSemantics>> = {
  drunk: "impairment",
  poisoned: "impairment",
  abilityLost: "noAbility",
  safeFromDemon: "demonProtection",
  cannotDie: "deathImmunity",
  protected: "genericProtection",
  soberHealthy: "judgment",
  registersFalsely: "judgment",
  fortuneTellerRedHerring: "storytellerFact",
};
export const effectSemanticsOf = (type: string): EffectSemantics | undefined =>
  Object.prototype.hasOwnProperty.call(APPROVED_EFFECT_SEMANTICS, type) ? APPROVED_EFFECT_SEMANTICS[type] : undefined;

/**
 * Canonical characters whose (pinned canonical) ability text can make SOME
 * participant register as something other than their Actual Role / Actual
 * Alignment. STRUCTURAL ONLY: it never computes a false registration; it only
 * makes the registration answer UNKNOWN (a Storyteller judgment) where such
 * an ability could apply. `self`: the bearer may misregister; `anyGood`: any
 * good participant may; `observerGood`: a good participant may, as seen by the
 * bearer.
 */
export const REGISTRATION_ALTERING: Readonly<Record<RoleId, "self" | "anyGood" | "observerGood">> = {
  recluse: "self",
  spy: "self",
  zombuul: "self",
  legion: "self",
  lycanthrope: "anyGood",
  // Phase 10F Slice 7: the Fortune Teller's "a good player registers as a
  // Demon to you" is no longer a blanket registration judgment -- it is the
  // authoritative `fortuneTellerRedHerring` fact (matrix Section 7), read by the
  // Fortune Teller evaluator through factHolders().
};

export type RulesQueryEnvironment = {
  registry: RoleRegistry;
  script: Pick<Script, "characters"> | null;
  semantics?: AbilitySemanticsRegistry;
  /** Precomputed active modifiers (defaults to activeModifiers(game, registry)). */
  modifiers?: readonly ModifierDefinition[];
};

export type RegistrationAnswer = {
  character: QueryAnswer<RoleId>;
  alignment: QueryAnswer<Alignment>;
};

export type RulesQuery = {
  game: StorytellerLobbyRecord;
  /** The current live Game Moment, or null outside Night/Day. */
  moment: () => LiveGameMoment | null;
  /** The occupied record bound to EXACTLY this participation instance, or null
   * (empty seat, another occupant -- stale). */
  participant: (binding: ParticipantBinding) => STPlayerRecord | null;
  isAlive: (binding: ParticipantBinding) => QueryAnswer<boolean>;
  /** Whether a stored Effect currently applies MECHANICALLY (derived). */
  effectApplies: (holder: ParticipantBinding, effect: EffectRecord) => QueryAnswer<boolean>;
  /** Drunk/Poisoned impairment (derived from applicable Effects). */
  impaired: (binding: ParticipantBinding) => QueryAnswer<boolean>;
  /** Whether the participant's ability functions: not impaired and not lost.
   * Death is reported separately (isAlive); whether an ability works while
   * dead is character semantics. */
  abilityFunctions: (binding: ParticipantBinding) => QueryAnswer<boolean>;
  /** Protection against a death of `cause`. `generic` Protected never answers
   * "protected" automatically. */
  protectedFrom: (binding: ParticipantBinding, cause: "demon" | "any") => QueryAnswer<boolean>;
  /** SOL-10F-C2: a deterministic, EXACT (never hashed) stamp of every piece of
   * authoritative Current State this query can consult when answering
   * `protectedFrom(binding, cause)` -- under THIS query's state, hypothetical
   * `assumingAlive` overlays included. Equal stamps mean the protection
   * question is being asked of the same state; any change that can alter
   * protection applicability changes it. A conservative superset: it may also
   * change after an unrelated mechanical change, never miss a relevant one.
   * Transient workflow identity only; never persisted. */
  protectionDependencyStamp: (binding: ParticipantBinding, cause: "demon" | "any") => string;
  /** What the participant registers as, to an `observer` character. */
  registration: (binding: ParticipantBinding, observer?: RoleId) => RegistrationAnswer;
  /** The nearest living participants on each side (skipping empty seats and
   * the dead), or null when there is none. */
  aliveNeighbours: (binding: ParticipantBinding) => QueryAnswer<{ left: ParticipantBinding | null; right: ParticipantBinding | null }>;
  /** Phase 10F Slice 7: the ACTIVE registry's definition of `roleId` (its
   * character type, canonical ownership...), or undefined. Definitions only --
   * never ability prose interpretation. */
  roleOf: (roleId: RoleId) => RoleDef | undefined;
  /** Occupied participants whose ACTUAL Role is `roleId`. */
  inPlay: (roleId: RoleId) => ParticipantBinding[];
  /** Phase 10F Slice 7: every current participant holding an ACTIVE Effect of
   * an approved `storytellerFact` type, with the Effect id (authoritative
   * Current State only -- never a Reminder). Unknown for any other type: a
   * custom Effect name is never treated as a fact. */
  factHolders: (type: string) => QueryAnswer<{ holder: ParticipantBinding; effectId: string }[]>;
  /** Life Events at `moment` (with honest coverage). */
  lifeEvents: (moment: LiveGameMoment, test?: (event: LifeEvent) => boolean) => LifeEventQueryResult;
  /** Phase 10G (PHASE10G Section 7): whether a REGISTERED game-scoped Rule
   * Fact currently applies, from this query's Current State (inside an ability
   * composition, the evolving working snapshot the query was built on). An
   * unregistered / custom type is UNKNOWN -- it never acquires a rule by name. */
  gameRuleFact: (type: string) => QueryAnswer<boolean>;
  /** The modifier gate for an evaluation of `roleId` touching `scopes`. */
  modifierGate: (roleId: RoleId, scopes: readonly HookScope[]) => ModifierGate;
  /** Phase 10F Slice 7: the same derived queries over a HYPOTHETICAL Life
   * state in which this participant is alive / dead -- the evolving working
   * state inside ONE resolution (e.g. a protection whose source died earlier
   * in the same ordered resolution). Pure; never persisted; a stale binding
   * returns this query unchanged. */
  assumingAlive: (binding: ParticipantBinding, alive: boolean) => RulesQuery;
};

export const bindingOf = (player: Pick<STPlayerRecord, "id" | "participantId">): ParticipantBinding =>
  ({ playerId: player.id, participantId: player.participantId ?? "" });

/** The occupied record at `binding.playerId`, only while it is still the
 * bound participation instance. Own-property safe. */
export function boundParticipant(game: StorytellerLobbyRecord, binding: ParticipantBinding | null | undefined): STPlayerRecord | null {
  if (!binding || typeof binding !== "object" || typeof binding.playerId !== "string" || typeof binding.participantId !== "string" || !binding.participantId) return null;
  if (!Object.prototype.hasOwnProperty.call(game.players, binding.playerId)) return null;
  const player = game.players[binding.playerId]!;
  return !player.isEmpty && player.participantId === binding.participantId ? player : null;
}

export function createRulesQuery(game: StorytellerLobbyRecord, environment: RulesQueryEnvironment): RulesQuery {
  const modifiers = environment.modifiers ?? activeModifiers(game, environment.registry);
  const participant = (binding: ParticipantBinding) => boundParticipant(game, binding);
  const seated = () => game.seatOrder.map((id) => game.players[id]).filter((p): p is STPlayerRecord => !!p && !p.isEmpty && !!p.participantId);

  /** Applicability with cycle detection: a revisited Effect is UNKNOWN. */
  const applies = (holder: STPlayerRecord, effect: EffectRecord, visiting: Set<string>): QueryAnswer<boolean> => {
    if (effect.state !== "active") return known(false); // a lifecycle DECISION, not derived
    const semantics = effectSemanticsOf(effect.type);
    if (!semantics) return unknown(`"${effect.type}" is a custom Effect: Silverwick applies no rule to it by name.`);
    if (semantics === "storytellerFact") return known(true); // as recorded; no source dependence
    const key = `${holder.participantId}:${effect.id}`;
    if (visiting.has(key)) return unknown("These Effects depend on each other: the Storyteller decides.");
    // A Storyteller's manual Effect (no origin participant) applies as recorded.
    const source = effect.sourceParticipant;
    if (!source) return known(true);
    if (source.kind !== "participant") return unknown("The Effect's origin predates participant identity.");
    // Whether a sourced Effect persists when its source stops functioning is
    // the SOURCE character's semantics; without a verified declaration it is
    // unknown -- never assumed either way.
    const sourceCharacter = effect.sourceCharacter;
    const descriptor = sourceCharacter ? resolveAbilitySemantics(sourceCharacter, environment.registry, environment.semantics) : null;
    const declared = descriptor?.kind === "supported"
      ? descriptor.descriptor.sourcedEffects?.find((entry) => entry.type === effect.type)?.persistence
      : undefined;
    if (declared === "independent") return known(true);
    if (declared !== "whileSourceFunctions") return unknown(`Whether this ${effect.type} Effect still applies depends on its source: the Storyteller decides.`);
    const sourcePlayer = boundParticipant(game, { playerId: source.playerId, participantId: source.participantId });
    if (!sourcePlayer) return unknown("The Effect's source is no longer in play: the Storyteller decides.");
    // Matrix Section 1 / 4: a persistent ability effect ends when its source
    // changes character -- the source no longer HAS that ability (derived; the
    // stored Effect is never rewritten).
    if (sourceCharacter && sourcePlayer.actualRole !== sourceCharacter) return known(false);
    // SOL-10F-A5: a KNOWN dead source no longer has its ability -- decisive
    // before any impairment / functioning uncertainty is even consulted. (A
    // departed / unseated source stays unknown, above.)
    if (!sourcePlayer.alive) return known(false);
    const next = new Set(visiting).add(key);
    const functioning = functions(sourcePlayer, next);
    if (!functioning.known) return functioning;
    return known(functioning.value);
  };

  /** Impairment of `player`: any applicable impairment Effect -> impaired;
   * any unknown one -> unknown; none -> not impaired. */
  const impairedOf = (player: STPlayerRecord, visiting: Set<string>, kinds: readonly EffectSemantics[]): QueryAnswer<boolean> => {
    let undetermined: string | null = null;
    for (const effect of player.effects) {
      const semantics = effectSemanticsOf(effect.type);
      if (!semantics || !kinds.includes(semantics)) continue;
      const answer = applies(player, effect, visiting);
      if (!answer.known) undetermined ??= answer.reason;
      else if (answer.value) return known(true);
    }
    return undetermined ? unknown(undetermined) : known(false);
  };

  const functions = (player: STPlayerRecord, visiting: Set<string>): QueryAnswer<boolean> => {
    if (player.effects.some((effect) => effect.state === "active" && effectSemanticsOf(effect.type) === "judgment" && effect.type === "soberHealthy")) {
      return unknown("A Sober & healthy Effect interacts with impairment: the Storyteller decides.");
    }
    const lost = impairedOf(player, visiting, ["noAbility"]);
    if (lost.known && lost.value) return known(false);
    const impaired = impairedOf(player, visiting, ["impairment"]);
    if (impaired.known && impaired.value) return known(false);
    if (!lost.known) return lost;
    if (!impaired.known) return impaired;
    return known(true);
  };

  /**
   * SOL-10F-C2: the protection-dependency snapshot of this query's state --
   * computed once per query (each `assumingAlive` overlay is its own query).
   * protectedFrom can reach, through Effect sources and their functioning, ANY
   * occupied participant, so the snapshot covers every one of them, ordered by
   * PlayerId: PlayerId, ParticipantId (replacement / departure), alive AS SEEN
   * BY THIS QUERY (hypothetical overlays included), Actual Role, and every
   * Effect record in order (id, type, lifecycle state, source character, source
   * participant's MECHANICAL identity -- SOL-10F-D2: kind, PlayerId and
   * ParticipantId, never the `nameAtTime` display snapshot -- expiry,
   * parameters) together with the source character's
   * persistence declaration as this query's environment resolves it.
   * Authoritative Current State only: never History, Reminders or prose
   * (names and notes are excluded). Exact JSON, so no collision handling is
   * needed.
   */
  let dependencySnapshot: string | undefined;
  const sortedKeys = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(sortedKeys);
    if (!value || typeof value !== "object") return value ?? null;
    return Object.fromEntries(Object.keys(value).sort().map((key) => [key, sortedKeys((value as Record<string, unknown>)[key])]));
  };
  /** SOL-10F-D2: an Effect source ParticipantRef's mechanical identity only
   * (the stored ref is never rewritten). */
  const sourceIdentity = (ref: EffectRecord["sourceParticipant"]): unknown => {
    if (!ref) return null;
    if (ref.kind === "participant") return [ref.kind, ref.playerId, ref.participantId];
    if (ref.kind === "legacy") return [ref.kind, ref.playerId];
    return sortedKeys(ref); // unreachable for schema-valid state; conservative
  };
  const declarationOf = (effect: EffectRecord): unknown => {
    if (!effect.sourceCharacter) return null;
    const resolved = resolveAbilitySemantics(effect.sourceCharacter, environment.registry, environment.semantics);
    return [resolved.kind, resolved.kind === "supported" ? resolved.descriptor.sourcedEffects?.find((entry) => entry.type === effect.type)?.persistence ?? null : null];
  };
  const protectionDependencies = (): string => dependencySnapshot ??= JSON.stringify(["protection-dependency-v1",
    Object.keys(game.players).sort().flatMap((playerId) => {
      const player = game.players[playerId];
      if (!player || player.isEmpty || !player.participantId) return [];
      return [[playerId, player.participantId, player.alive, player.actualRole, (player.effects ?? []).map((effect) => [
        effect.id, effect.type, effect.state, effect.sourceCharacter ?? null, sourceIdentity(effect.sourceParticipant),
        sortedKeys(effect.expiry), sortedKeys(effect.parameters), declarationOf(effect)])]];
    })]);

  const require = <T,>(binding: ParticipantBinding, run: (player: STPlayerRecord) => QueryAnswer<T>): QueryAnswer<T> => {
    const player = participant(binding);
    return player ? run(player) : unknown("That participant is no longer in this seat.");
  };

  const self: RulesQuery = {
    game,
    moment: () => currentLiveMoment(game),
    participant,
    isAlive: (binding) => require(binding, (player) => known(player.alive)),
    effectApplies: (holder, effect) => require(holder, (player) => applies(player, effect, new Set())),
    impaired: (binding) => require(binding, (player) => impairedOf(player, new Set(), ["impairment"])),
    abilityFunctions: (binding) => require(binding, (player) => functions(player, new Set())),
    protectedFrom: (binding, cause) => require(binding, (player) => {
      let undetermined: string | null = null;
      for (const effect of player.effects) {
        const semantics = effectSemanticsOf(effect.type);
        if (semantics !== "deathImmunity" && semantics !== "demonProtection" && semantics !== "genericProtection") continue;
        if (semantics === "demonProtection" && cause !== "demon") continue;
        const answer = applies(player, effect, new Set());
        if (!answer.known) { undetermined ??= answer.reason; continue; }
        if (!answer.value) continue;
        if (semantics === "genericProtection") {
          undetermined ??= "A generic Protected marker never decides death automatically: the Storyteller decides.";
          continue;
        }
        return known(true);
      }
      return undetermined ? unknown(undetermined) : known(false);
    }),
    protectionDependencyStamp: () => protectionDependencies(),
    registration: (binding, observer) => {
      const player = participant(binding);
      if (!player) {
        const stale = unknown<never>("That participant is no longer in this seat.");
        return { character: stale, alignment: stale };
      }
      const judgment = (reason: string): RegistrationAnswer => ({ character: unknown(reason), alignment: unknown(reason) });
      const role = environment.registry.get(player.actualRole);
      if (!role || !isCanonicalRole(role)) return judgment("Registration of a non-canonical character is the Storyteller's decision.");
      if (player.effects.some((effect) => effect.state === "active" && effect.type === "registersFalsely")) {
        return judgment("A Registers falsely Effect applies: the Storyteller decides.");
      }
      if (REGISTRATION_ALTERING[player.actualRole] === "self") return judgment(`The ${role.name} may register falsely: the Storyteller decides.`);
      const good = player.actualAlignment === "good";
      for (const other of seated()) {
        const scope = REGISTRATION_ALTERING[other.actualRole];
        if (!scope || scope === "self") continue;
        if (scope === "anyGood" && good) return judgment(`The ${environment.registry.get(other.actualRole)?.name ?? other.actualRole} may make a good player register falsely: the Storyteller decides.`);
        if (scope === "observerGood" && good && observer === other.actualRole) return judgment("A good player may register falsely to this character: the Storyteller decides.");
      }
      const gate = gateEvaluation(modifiers, observer ?? player.actualRole, ["registration"], game);
      if (gate.kind !== "clear") return judgment("A modifier may change registration: the Storyteller decides.");
      return {
        character: known(player.actualRole),
        alignment: player.actualAlignment ? known(player.actualAlignment) : unknown("The Actual Alignment is unresolved."),
      };
    },
    aliveNeighbours: (binding) => require(binding, (player) => {
      const ring = seated();
      const index = ring.findIndex((p) => p.id === player.id);
      if (index < 0) return unknown("That participant is not in the seat order.");
      const find = (step: 1 | -1): ParticipantBinding | null => {
        for (let offset = 1; offset < ring.length; offset++) {
          const candidate = ring[(((index + step * offset) % ring.length) + ring.length) % ring.length]!;
          if (candidate.id !== player.id && candidate.alive) return bindingOf(candidate);
        }
        return null;
      };
      return known({ left: find(-1), right: find(1) });
    }),
    roleOf: (roleId) => (typeof roleId === "string" && roleId ? environment.registry.get(roleId) : undefined),
    inPlay: (roleId) => seated().filter((p) => p.actualRole === roleId).map(bindingOf),
    factHolders: (type) => effectSemanticsOf(type) !== "storytellerFact"
      ? unknown(`"${type}" is not an approved Storyteller fact.`)
      : known(seated().flatMap((p) => p.effects.filter((e) => e.type === type && e.state === "active").map((e) => ({ holder: bindingOf(p), effectId: e.id })))),
    lifeEvents: (moment, test) => lifeEventsAt(game, moment, test),
    gameRuleFact: (type) => gameRuleFactDefinition(type)
      ? known(gameRuleFactActive(game, type))
      : unknown(`"${typeof type === "string" ? type : "?"}" is not a registered Rule Fact: Silverwick applies no rule to it.`),
    modifierGate: (roleId, scopes) => gateEvaluation(modifiers, roleId, scopes, game),
    assumingAlive: (binding, alive) => {
      const player = participant(binding);
      if (!player) return self;
      return createRulesQuery({ ...game, players: { ...game.players, [player.id]: { ...player, alive } } }, { ...environment, modifiers });
    },
  };
  return self;
}
